import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import { Bolt, Calendar, Check, Copy, Info } from "../../components/icons.js";

/**
 * Candidate · schedule step. The interview is async — nobody is waiting on the other side —
 * so "Start now" is the primary path. The fallback is a native day + time picker (no slot
 * grid): the candidate names any moment ≥15 min out and ≤7 days ahead, in their own timezone.
 */

export const VIEWER_TZ: string = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
  } catch {
    return "Asia/Kolkata";
  }
})();

export function dayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: VIEWER_TZ,
  });
}
export function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZone: VIEWER_TZ });
}

const MIN_LEAD_MIN = 15;
const MAX_LEAD_DAYS = 7;
// Bookable window mirrors the server (inviteSchedule.ts): on the hour/half-hour, 08:00–20:30 in the
// viewer's timezone. Offering only these as a dropdown means the candidate can never pick a slot the
// server would reject.
const BOOK_START_HOUR = 8;
const BOOK_END_HOUR = 20; // last slot is 20:30
const TIME_SLOTS: string[] = (() => {
  const out: string[] = [];
  for (let h = BOOK_START_HOUR; h <= BOOK_END_HOUR; h++) {
    for (const m of [0, 30]) out.push(`${`${h}`.padStart(2, "0")}:${`${m}`.padStart(2, "0")}`);
  }
  return out;
})();
function slotLabel(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = (h ?? 0) < 12 ? "AM" : "PM";
  const h12 = (h ?? 0) % 12 === 0 ? 12 : (h ?? 0) % 12;
  return `${h12}:${`${m ?? 0}`.padStart(2, "0")} ${ampm}`;
}

/** yyyy-mm-dd for the <input type="date"> min/max, in the viewer's local calendar. */
function dateInputValue(d: Date): string {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * "Don't lose this link" card: copy button (works today) + preferred email capture
 * (stored now, delivered once the messaging channel is live — we never claim it sent).
 */
export function SaveLinkCard({ token, savedEmail }: { token: string; savedEmail: string | null }): ReactNode {
  const [email, setEmail] = useState("");
  const [copied, setCopied] = useState(false);
  const contact = useMutation({
    mutationFn: (e: string) => api.post<{ saved: boolean; email: string }>(`/invite/${token}/contact`, { email: e }),
  });
  const shown = contact.data?.email ?? savedEmail;

  return (
    <div className="card pad t1c-savelink" style={{ marginTop: 16 }}>
      <div style={{ fontWeight: 650, fontSize: 14, marginBottom: 4 }}>Keep your link safe</div>
      <p className="mut sm" style={{ margin: "0 0 10px" }}>
        This link is your key — reopen it anytime to start, continue, or reschedule.
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <button
          className="btn sm ghost"
          onClick={() => {
            void navigator.clipboard?.writeText(window.location.href).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 2500);
            });
          }}
        >
          {copied ? <><Check /> Copied</> : <><Copy /> Copy link</>}
        </button>
        <input
          className="input"
          style={{ flex: "1 1 180px", minWidth: 160 }}
          type="email"
          placeholder={shown ?? "Best email for this link"}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <button
          className="btn sm"
          disabled={!email.includes("@") || contact.isPending}
          onClick={() => contact.mutate(email)}
        >
          {contact.isPending ? "Saving…" : "Save email"}
        </button>
      </div>
      {contact.isSuccess && (
        <p className="mut sm" style={{ margin: "8px 0 0" }}>
          Saved — we'll use {contact.data.email} for reminders once messaging goes live. For now, the copied link is
          the reliable way back.
        </p>
      )}
      {contact.isError && (
        <p className="sm" style={{ margin: "8px 0 0", color: "var(--crit)" }}>
          Couldn't save that address — check it and try again.
        </p>
      )}
    </div>
  );
}

export function ScheduleStep({
  token,
  savedEmail = null,
  onBack,
}: {
  token: string;
  savedEmail?: string | null;
  onBack?: () => void;
}): ReactNode {
  const queryClient = useQueryClient();
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");

  const now = useMemo(() => new Date(), []);
  const minDate = dateInputValue(now);
  const maxDate = dateInputValue(new Date(now.getTime() + MAX_LEAD_DAYS * 86_400_000));

  // Combine the local date+time inputs into an absolute instant, then validate the window.
  const picked: { iso: string; error: string | null } | null = useMemo(() => {
    if (!date || !time) return null;
    const dt = new Date(`${date}T${time}`);
    if (Number.isNaN(dt.getTime())) return { iso: "", error: "That date and time didn't parse — pick again." };
    // Mirror the server's slot rules so we never let the candidate confirm a time it will reject.
    const [hh, mm] = time.split(":").map(Number);
    if (mm !== 0 && mm !== 30)
      return { iso: dt.toISOString(), error: "Pick a time on the hour or half-hour." };
    if ((hh ?? 0) < BOOK_START_HOUR || (hh ?? 0) > BOOK_END_HOUR)
      return { iso: dt.toISOString(), error: "Interviews can be booked between 8:00 AM and 9:00 PM." };
    const diffMin = (dt.getTime() - Date.now()) / 60_000;
    if (diffMin < MIN_LEAD_MIN)
      return { iso: dt.toISOString(), error: `Please pick a time at least ${MIN_LEAD_MIN} minutes from now.` };
    if (diffMin > MAX_LEAD_DAYS * 24 * 60)
      return { iso: dt.toISOString(), error: `Please pick a time within the next ${MAX_LEAD_DAYS} days.` };
    return { iso: dt.toISOString(), error: null };
  }, [date, time]);
  const validSlot = picked && picked.error === null ? picked.iso : null;

  const schedule = useMutation({
    mutationFn: (body: { slot?: string; tz?: string; startNow?: boolean }) =>
      api.post<unknown>(`/invite/${token}/schedule`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["invite", token] });
      onBack?.();
    },
  });

  return (
    <div className="cx-step">
      <h1 className="t1c-h1">Start now, or pick a time</h1>
      <p className="mut t1c-lede">
        About 30 minutes, fully online — nobody is waiting on you, so you can start right away or pick a time that
        suits you.
      </p>

      {/* Primary path: just do it now. */}
      <div className="t1c-startnow">
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 260px" }}>
            <div style={{ fontWeight: 600 }}>Start your interview now</div>
            <div className="mut sm">Quiet spot, ~30 uninterrupted minutes, camera + mic — that's all you need.</div>
          </div>
          <button
            className="btn primary"
            disabled={schedule.isPending}
            onClick={() => schedule.mutate({ startNow: true })}
          >
            <Bolt /> {schedule.isPending ? "Getting ready…" : "Start now"}
          </button>
        </div>
      </div>

      {/* Fallback: pick any day & time (native pickers, no slot grid). */}
      <h3 className="t1c-h3" style={{ margin: "22px 0 4px" }}>Or book a time</h3>
      <div className="mut sm" style={{ margin: "0 0 10px" }}>
        Shown in your timezone — {VIEWER_TZ.replace(/_/g, " ")}
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <div className="field" style={{ flex: "1 1 180px", marginBottom: 0 }}>
          <label htmlFor="cx-date">Day</label>
          <input
            id="cx-date"
            className="input"
            type="date"
            value={date}
            min={minDate}
            max={maxDate}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="field" style={{ flex: "1 1 140px", marginBottom: 0 }}>
          <label htmlFor="cx-time">Time</label>
          <select
            id="cx-time"
            className="input"
            value={time}
            onChange={(e) => setTime(e.target.value)}
          >
            <option value="">Select a time…</option>
            {TIME_SLOTS.map((s) => (
              <option key={s} value={s}>
                {slotLabel(s)}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="mut sm" style={{ margin: "8px 0 0" }}>
        Between 8:00 AM and 9:00 PM your time · from {MIN_LEAD_MIN} minutes out to {MAX_LEAD_DAYS} days ahead.
      </div>

      {picked?.error && (
        <div className="banner warn" style={{ marginTop: 12 }}>
          <Info />
          <span>{picked.error}</span>
        </div>
      )}
      {schedule.isError && (
        <div className="banner crit" style={{ marginTop: 12 }}>
          <Info />
          <span>Couldn't book that time. Adjust it and try again — or just start now.</span>
        </div>
      )}

      <div className="t1c-actions">
        <button
          className="btn primary"
          disabled={validSlot === null || schedule.isPending}
          onClick={() => validSlot && schedule.mutate({ slot: validSlot, tz: VIEWER_TZ })}
        >
          <Calendar />{" "}
          {schedule.isPending
            ? "Booking…"
            : validSlot
              ? `Confirm ${timeLabel(validSlot)} · ${dayLabel(validSlot)}`
              : "Confirm time"}
        </button>
        {onBack && (
          <button className="btn ghost" onClick={onBack}>
            Keep my current booking
          </button>
        )}
      </div>

      <SaveLinkCard token={token} savedEmail={savedEmail} />
    </div>
  );
}
